import { createContext, useContext, useEffect, useState } from 'react'
import { subscribeSharedSse } from '../sharedSse'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import {
  HOSTS_EVENTS_PATH,
  hostError,
  type Host,
  type HostCommand,
  type HostCommandKind,
  type HostEventCommand,
  type HostEventHost,
  type HostEventJob,
  type HostEventReboot,
  type HostNetwork,
  type InstallInfo,
  type NetWarning,
  type RebootCheckName,
  type RebootWatch,
  type InstallJob,
  type InstallStage,
  type InstallStageName,
  type NodeInfo,
} from '../api'
import { spkiShort } from './network/format'

/*
 * Hosts, first installs and agent commands (M2.4, docs/m2-provisioning.md
 * §4, §6, §11): query keys, names, client-side checks, error texts by code
 * and the live updates from GET /api/admin/hosts/events.
 */

export const HOSTS_KEY = ['admin-hosts'] as const
export const PROV_KEYS_KEY = ['admin-provisioning-keys'] as const
export const TOTP_KEY = ['admin-totp'] as const
export const hostKey = (id: string) => ['admin-host', id] as const
export const hostJobsKey = (id: string) => ['admin-host-jobs', id] as const
export const jobKey = (id: string) => ['admin-install-job', id] as const
/** Same key as NODES_KEY in Nodes.tsx (kept here to avoid an import cycle). */
export const NODES_KEY = ['admin-nodes'] as const

/** Stage names as people read them, with what the stage does and how long it may take. */
export const STAGE_INFO: Record<InstallStageName, { title: string; about: string }> = {
  dns: { title: 'DNS record', about: "Sets the node's name up (the record, when the center manages the zone). Does not wait: the name settles while the next stages run" },
  ssh_connect: { title: 'SSH connection', about: "Logs in with the center's key (30 s)" },
  host_key: { title: 'Host key check', about: "Compares the server's SSH key with the fingerprint you gave" },
  preflight: { title: 'Server checks', about: 'Ubuntu 22.04 or newer, amd64, free ports 80, 443, 1935, 1936, 2 GB of disk, sudo without a password' },
  docker: { title: 'Docker', about: "Docker and unattended-upgrades from Ubuntu's repositories (up to 10 min)" },
  files: { title: 'Release files', about: 'Copies the node release and checks every file (up to 10 min)' },
  install: { title: 'Node install', about: 'Loads the images and installs the media node; it does not start yet' },
  agent: { title: 'Host agent', about: 'Sets up the agent that takes over from SSH' },
  agent_online: { title: 'Agent connects', about: "Waits for the agent's encrypted channel to the center (up to 2 min)" },
  dns_ready: { title: 'DNS name resolves', about: "Checks that the node's name resolves to its address on 1.1.1.1 before the node starts and asks Let's Encrypt for a certificate (up to 10 min)" },
  join: { title: 'Node joins', about: "Starts the media node and waits until it joins with a Let's Encrypt certificate (up to 20 min)" },
  relay_key: { title: 'Relay key registered · node in peers list', about: 'The node sends its relay key; the center adds it to the peers list and waits until every node acknowledges it. Until then no route uses the node' },
  cleanup: { title: 'Cleanup', about: "Removes temporary files and, if you chose so, the center's SSH key" },
  reboot: { title: 'Reboot', about: 'Schedules the reboot you asked for, or notes that the server asks for one' },
  after_reboot: { title: 'Back after reboot', about: 'Waits for the agent and the media node and checks containers, node online, BBR, fq (up to 10 min)' },
}

/** While a stage runs: what the admin should expect. */
export const STAGE_WAIT: Partial<Record<InstallStageName, string>> = {
  dns_ready: "Waiting for the node's name to resolve on 1.1.1.1. If the record is right at the zone's own servers but this takes long, 1.1.1.1 caches an older answer: purge it at one.one.one.one/purge-cache.",
  docker: 'Installing Docker from the Ubuntu repositories. This can take up to 10 minutes.',
  files: 'Copying the release to the server. This can take up to 10 minutes on a slow link.',
  agent_online: 'Waiting for the agent to connect to the center. A server behind NAT that cannot reach the center fails here.',
  join: "Waiting for the node to join. The Let's Encrypt certificate can take up to 20 minutes.",
  relay_key: 'Waiting for every node to acknowledge the peers list with the new relay key. The node stays out of routing until then.',
  after_reboot: 'Waiting for the server to reboot and the node to come back. This takes up to 10 minutes after the reboot time.',
}

export const STAGE_STATUS_TEXT: Record<InstallStage['status'], string> = {
  pending: 'Waiting',
  running: 'Running',
  done: 'Done',
  failed: 'Failed',
  skipped: 'Skipped',
}

/** Commands: button label and what the record is called. */
export const COMMAND_LABEL: Record<HostCommandKind, string> = {
  status: 'Status',
  diagnostics: 'Diagnostics',
  restart: 'Restart',
  restart_node: 'Restart node',
  update: 'Update',
  os_update: 'Security updates',
  install_packages: 'Install packages',
  remove_ssh_access: 'Revoke SSH access',
  reboot: 'Reboot',
  reboot_cancel: 'Cancel reboot',
  unenroll: 'Delete host',
  deploy: 'Update',
  redeploy: 'Redeploy',
  rollback: 'Roll back',
  rebind: 'Rebind',
  relay_enable: 'Enable relay',
  relay_disable: 'Disable relay',
  drain: 'Drain',
  undrain: 'Undrain',
}

/** The word typed to confirm (docs/m2-provisioning.md §6); checked here and by controlapi. */
export const CONFIRM_WORD: Partial<Record<HostCommandKind, string>> = { reboot: 'REBOOT', rebind: 'REBIND', unenroll: 'DELETE' }

/** Commands that need no second factor. */
export const NO_FACTOR: HostCommandKind[] = ['status', 'diagnostics']

export const COMMAND_STATE_TEXT: Record<HostCommand['state'], string> = {
  sent: 'Sent',
  ok: 'Done',
  failed: 'Failed',
  timeout: 'Waiting for the agent',
}

// --- client-side checks, the same as the provisioner's (internal/provision/install.go) ---

export const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,30}$/
export const SSH_USER_RE = /^[a-z_][a-z0-9_-]{0,31}$/
export const REGION_RE = /^[a-z0-9-]{1,32}$/
export const FINGERPRINT_RE = /^SHA256:[A-Za-z0-9+/]{43}$/
export const HOSTNAME_RE = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i
/** install_packages: Ubuntu package names, at most 20 (docs/m2-provisioning.md §6). */
export const PACKAGE_RE = /^[a-z0-9][a-z0-9+.-]{0,62}$/
export const MAX_PACKAGES = 20

/** IPv4 ranges a node's address is never in (loopback, private, link-local, CGNAT, reserved, documentation, multicast and above). */
const NON_PUBLIC: [number, number][] = [
  ['0.0.0.0', 8],
  ['10.0.0.0', 8],
  ['100.64.0.0', 10],
  ['127.0.0.0', 8],
  ['169.254.0.0', 16],
  ['172.16.0.0', 12],
  ['192.0.0.0', 24],
  ['192.0.2.0', 24],
  ['192.168.0.0', 16],
  ['198.18.0.0', 15],
  ['198.51.100.0', 24],
  ['203.0.113.0', 24],
  ['224.0.0.0', 3],
].map(([a, bits]) => [ipv4(a as string)!, bits as number])

function ipv4(s: string): number | undefined {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(s.trim())
  if (!m) return undefined
  const parts = m.slice(1, 5) as [string, string, string, string]
  if (parts.some((x) => Number(x) > 255 || (x.length > 1 && x.startsWith('0')))) return undefined
  const [a, b, c, d] = parts.map(Number) as [number, number, number, number]
  return ((a << 24) | (b << 16) | (c << 8) | d) >>> 0
}

/** '' when s is a public IPv4 address, else what is wrong with it. */
export function addressProblem(s: string): string {
  const ip = ipv4(s)
  if (ip === undefined) return "The server's IPv4 address, e.g. 51.15.20.30"
  for (const [net, bits] of NON_PUBLIC) {
    const mask = bits === 0 ? 0 : (~0 << (32 - bits)) >>> 0
    if (((ip & mask) >>> 0) === net) return 'Must be a public address: not private, loopback, link-local, CGNAT or reserved'
  }
  return ''
}

/** Release numbers the host is behind the center's (0 when unknown or up to date). */
export function releasesBehind(h: Pick<Host, 'release_no'>, info?: InstallInfo): number {
  if (!info?.release_no || h.release_no === undefined || h.release_no === null) return 0
  return Math.max(0, info.release_no - h.release_no)
}

/** Why first installs are off, as people read it. install.reason is free text, so it is matched loosely. */
export function installOffText(info: InstallInfo | undefined): string {
  const r = info?.reason ?? ''
  if (/release/i.test(r)) {
    return 'This center has no node release built in (a local build of the provisioner image). First installs and updates work with the image CI builds.'
  }
  if (/not configured/i.test(r) || !info) {
    return 'Automatic installs are not configured on this center (PROVISIONER_CENTER_DOMAIN and PROVISIONER_ACME_EMAIL in .env). See docs/deploy.md, "Провижининг узлов".'
  }
  return r
}

/** The DNS part of install.reason while installs are on (Cloudflare zone not readable, …). */
export function dnsProblem(info: InstallInfo | undefined): string {
  if (!info?.enabled || !info.reason) return ''
  const i = info.reason.indexOf('DNS:')
  return i >= 0 ? info.reason.slice(i) : info.reason
}

/** Text of a failed hosts request, by code (never by the server's text, except where it names a field). */
export function hostErrorText(e: unknown): string {
  const h = hostError(e)
  if (!h) return (e as Error)?.message ?? String(e)
  switch (h.code) {
    case 'totp_not_enrolled':
      return 'Your account has no second factor yet. On the center run: controlapi provisioner totp-enroll --admin <your admin id>'
    case 'totp_required':
      return 'Enter the code from your authenticator app.'
    case 'totp_invalid':
      return 'Wrong code. Five wrong codes in a row lock confirmations for 15 minutes.'
    case 'totp_locked':
      return `Too many wrong codes: confirmations are locked${h.retry_after_s ? ` for ${Math.ceil(h.retry_after_s / 60)} min` : ''}.`
    case 'confirm_required':
      return `Type ${h.confirm ?? 'the word'} to confirm.`
    case 'live_on_node':
      return `${liveText(h.live)} run on this node: they drop with the reboot. Confirm to reboot anyway.`
    case 'offline':
      return 'The host agent is not connected. Try again when it is back online.'
    case 'busy':
      // Install jobs and exclusive commands (update, rebind, relay) share the code; the server names which.
      return /still running on this host/.test(h.error ?? '')
        ? 'Another command that changes this node is still running. Wait for its result below: nothing was sent twice.'
        : 'An install is already running for this host. Wait until it finishes or fails.'
    case 'no_node':
      return 'The host has no media node in the center yet: install it first.'
    case 'node_revoked':
      return 'The host’s media node is revoked: rebind it to give it a new identity.'
    case 'not_failed':
      return 'Only a failed install can be retried, and this one is running or done.'
    case 'no_release':
      return installOffText({ enabled: false, reason: 'no release' })
    case 'not_configured':
      return installOffText({ enabled: false, reason: 'not configured' })
    case 'not_found':
      return 'Not found: the host or the install is gone (finished installs are kept 7 days).'
    case 'bad_request':
      return `Check the form: ${h.error}`
  }
  if (h.status === 404) return 'Provisioning is not configured on this center (PROVISIONER_URL).'
  if (h.status === 502) return 'The provisioner is unavailable. Try again in a minute.'
  return h.error
}

/** "2 live streams and 3 outputs". */
export function liveText(l?: { streams: number; outputs: number }): string {
  if (!l) return 'Broadcasts'
  const n = (k: number, one: string) => `${k} ${one}${k === 1 ? '' : 's'}`
  return `${n(l.streams, 'live stream')} and ${n(l.outputs, 'output')}`
}

// --- reboots and the network (docs/edge-node.md, «Сеть хоста») ---

/** Reboot delays the console offers (minutes). */
export const REBOOT_DELAYS = [5, 15, 30, 60, 120, 240]

export const REBOOT_STATE_TEXT: Record<RebootWatch['state'], string> = {
  scheduled: 'Reboot scheduled',
  checking: 'Back after the reboot, checking',
  ok: 'Back after the reboot: all checks passed',
  failed: 'Not healthy after the reboot',
  cancelled: 'Reboot cancelled',
}

export const REBOOT_CHECK_TEXT: Record<RebootCheckName, string> = {
  agent: 'Agent back',
  containers: 'Containers running',
  node_online: 'Media node online',
  bbr: 'BBR on the node',
  fq: 'qdisc fq',
  reboot_flag: 'No reboot required',
}

/** What each network warning means, for the facts table. */
export const NET_WARNING_TEXT: Record<NetWarning, string> = {
  not_bbr: "The node's connections do not run BBR",
  no_fq: 'The NIC has no fq qdisc',
  queues: 'The NIC uses fewer queues than it and the CPUs allow',
  time_not_synced: 'The clock is not synchronized',
  docker_proxy: 'External connections go through docker-proxy (IPv6 without Docker IPv6?)',
}

/** The warnings of a network report, also derived when an older agent sent none. */
export function netWarnings(n?: HostNetwork): NetWarning[] {
  if (!n) return []
  if (n.warnings) return n.warnings
  const w: NetWarning[] = []
  if (n.node_cc && n.node_cc !== 'bbr') w.push('not_bbr')
  if (n.dev && n.qdisc && !n.fq) w.push('no_fq')
  const want = Math.min(n.queues_max ?? 0, n.cpus || Infinity)
  if (n.queues && n.queues < want) w.push('queues')
  if (n.time_synced === false) w.push('time_not_synced')
  if ((n.docker_proxy_external ?? 0) > 0) w.push('docker_proxy')
  return w
}

// --- durations ---

/** "0:42", "12:03", "1:02:03". */
export function clock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`
}

/** How long a stage took, or has been running (ticks with now). */
export function stageDuration(s: InstallStage, now: number): number | undefined {
  if (!s.started_at) return undefined
  const end = s.finished_at ? Date.parse(s.finished_at) : s.status === 'running' ? now : undefined
  if (end === undefined) return undefined
  return Math.max(0, end - Date.parse(s.started_at))
}

/** The last 4 KB of a stage's output (the server already cuts it; this guards the page). */
export const tail4k = (s: string) => (s.length > 4096 ? s.slice(-4096) : s)

/** Upserts a command into a host's list (newest first, by seq). */
export function withCommand(h: Host, c: HostCommand): Host {
  const rest = (h.commands ?? []).filter((x) => x.seq !== c.seq)
  return { ...h, commands: [c, ...rest].sort((a, b) => b.seq - a.seq) }
}

function upsertJob(list: InstallJob[] | undefined, j: InstallJob): InstallJob[] | undefined {
  if (!list) return list
  const i = list.findIndex((x) => x.id === j.id)
  if (i < 0) return [j, ...list]
  const out = list.slice()
  out[i] = j
  return out
}

/** Applies one hosts event to the caches. Exported for the unit of the page that also polls. */
export function applyHostEvent(qc: QueryClient, name: string, raw: string) {
  let d: unknown
  try {
    d = JSON.parse(raw)
  } catch {
    return
  }
  if (name === 'host.host') {
    const e = d as HostEventHost
    // The agent was just seen: connecting now, or dropped just now.
    const patch = (h: Host): Host => ({ ...h, online: e.online, last_seen: new Date().toISOString() })
    qc.setQueryData<Host[]>(HOSTS_KEY, (l) => l?.map((h) => (h.id === e.host_id ? patch(h) : h)))
    qc.setQueryData<Host>(hostKey(e.host_id), (h) => (h ? patch(h) : h))
    return
  }
  if (name === 'host.job') {
    const e = d as HostEventJob
    qc.setQueryData(jobKey(e.job.id), e.job)
    qc.setQueryData<InstallJob[]>(hostJobsKey(e.host_id), (l) => upsertJob(l, e.job))
    void qc.invalidateQueries({ queryKey: HOSTS_KEY })
    void qc.invalidateQueries({ queryKey: hostKey(e.host_id) })
    // join adds the media node; the end of a job changes the host's release.
    const joined = e.job.stages.some((s) => s.name === 'join' && s.status === 'done')
    if (joined || e.job.state !== 'running') void qc.invalidateQueries({ queryKey: NODES_KEY })
    return
  }
  if (name === 'host.reboot') {
    const e = d as HostEventReboot
    qc.setQueryData<Host>(hostKey(e.host_id), (h) => (h ? { ...h, reboot: e.reboot } : h))
    qc.setQueryData<Host[]>(HOSTS_KEY, (l) => l?.map((h) => (h.id === e.host_id ? { ...h, reboot: e.reboot } : h)))
    // A check after a reboot ran status: the facts changed.
    if (e.reboot.state !== 'scheduled') void qc.invalidateQueries({ queryKey: hostKey(e.host_id) })
    return
  }
  if (name === 'host.command') {
    const e = d as HostEventCommand
    qc.setQueryData<Host>(hostKey(e.host_id), (h) => (h ? withCommand(h, e.command) : h))
    if (e.command.state === 'ok' || e.command.state === 'failed') {
      void qc.invalidateQueries({ queryKey: HOSTS_KEY })
      void qc.invalidateQueries({ queryKey: hostKey(e.host_id) })
      if (e.command.kind === 'rebind' || e.command.kind === 'unenroll') void qc.invalidateQueries({ queryKey: NODES_KEY })
    }
  }
}

/**
 * Whether the hosts event stream is up. Queries of hosts, jobs and commands
 * poll only while it is down (pollWhileDown), so events and polling never
 * both run.
 */
export const HostsLiveContext = createContext(false)

/** refetchInterval for a hosts query: ms while the stream is down, off while it is up. */
export function usePollWhileDown(ms: number): number | false {
  return useContext(HostsLiveContext) ? false : ms
}

const HOST_EVENT_NAMES = ['host.host', 'host.job', 'host.command', 'host.reboot'] as const

/**
 * Live hosts, installs and commands while the page is open (only when
 * provisioning answers). The console tabs of this browser share one stream
 * (sharedSse.ts): it reconnects by itself, waits Retry-After when the center
 * refuses it by its limit (429), and closes while every tab is hidden. Returns
 * whether the stream is up.
 */
export function useHostEvents(enabled: boolean): boolean {
  const qc = useQueryClient()
  const [live, setLive] = useState(false)
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return
    // one stream for every console tab of this browser (sharedSse.ts); the leader holds it
    const leave = subscribeSharedSse({
      path: HOSTS_EVENTS_PATH,
      key: 'strevix-live-hosts',
      events: HOST_EVENT_NAMES,
      onEvent: (e) => applyHostEvent(qc, e.name, e.data),
      onState: (s) => {
        const up = s === 'open'
        setLive(up)
        // anything that changed while the stream was down
        if (up) void qc.invalidateQueries({ queryKey: HOSTS_KEY })
      },
    })
    return () => {
      leave()
      setLive(false)
    }
  }, [enabled, qc])
  return live
}

/**
 * The relay_key stage (routing network R0, design #r0 (d)): "relay key registered · node in peers list".
 * The provisioner does not report it yet (InstallStage.name has no relay_key in the contract), so after
 * join the console derives it from the node's relay state (NodeInfo.relay, GET /api/admin/nodes) and puts
 * it right after join. A job that carries its own relay_key stage is shown as it is. Null when there is
 * nothing to show: a center without the relay API (no relay field) or the node not in the list yet.
 */
export function relayKeyStage(j: InstallJob, n: NodeInfo | undefined): InstallStage | null {
  if (j.stages.some((s) => s.name === 'relay_key')) return null
  const join = j.stages.find((s) => s.name === 'join')
  if (!join || !n?.relay) return null
  const r = n.relay
  if (join.status !== 'done') return { name: 'relay_key', status: 'pending' }
  if (r.state === 'disabled') return { name: 'relay_key', status: 'skipped', text: 'Relay is switched off for this node or the center' }
  if (r.state === 'unsupported') return { name: 'relay_key', status: 'skipped', text: 'The node runs a version without relay' }
  if (r.in_peers) {
    const text = r.spki ? `Key ${spkiShort(r.spki)} in peers list v${r.peers_version_acked}` : `In peers list v${r.peers_version_acked}`
    return { name: 'relay_key', status: 'done', text }
  }
  if (r.peers_rejected) {
    return { name: 'relay_key', status: 'running', text: `The node refused peers list v${r.peers_rejected.version}: ${r.peers_rejected.reason}` }
  }
  if (r.spki) return { name: 'relay_key', status: 'running', text: `Key ${spkiShort(r.spki)} registered · waiting for every node to acknowledge peers list v${r.peers_version} (node at v${r.peers_version_acked})` }
  return { name: 'relay_key', status: 'running', text: n.status === 'online' ? 'Waiting for the node to send its relay key' : 'Waiting for the node to connect' }
}

/** The stages to show: the job's, with the derived relay_key after join. */
export function stagesWithRelayKey(j: InstallJob, n: NodeInfo | undefined): { stages: InstallStage[]; derived: boolean } {
  const rk = relayKeyStage(j, n)
  if (!rk) return { stages: j.stages, derived: false }
  const i = j.stages.findIndex((s) => s.name === 'join')
  return { stages: [...j.stages.slice(0, i + 1), rk, ...j.stages.slice(i + 1)], derived: true }
}
